/* eslint-disable complexity */
import React, { useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';

import { CompIcon } from 'common/react/components/CompIcon';
import { getTranslation } from 'common/react/utils/translations';
import { SvgIcon } from 'common/react/components/SvgIcon';

import { FixtureTeam } from './FixtureTeam';
import { FixtureScore } from './FixtureScore';
import { FixtureVenue } from './FixtureVenue';
import { FixturePosition } from './FixturePosition';

import { useGetLadderQuery } from 'store/modules/api/afl-api';
import { FixtureSummary } from './FixtureSummary';

import { getLadderPosition } from '../../utils/get-ladder-position';
import { FixtureSoldOut } from './FixtureSoldOut';
import { FixturePrematchLabel } from './FixturePrematchLabel';
import { FixtureCountContext } from '../fixtures-list/FixturesList';

const getConferenceLabel = (match, isAway = false) => {
    const conference = isAway
        ? match?.away?.conference
        : match?.home?.conference;

    if (conference?.toUpperCase().includes('CONFERENCE')) {
        return conference.replace(/\s+/g, '-').toLowerCase();
    }

    return null;
};

export const FixtureDetails = ({
    match,
    liveMatch,
    link,
    matchStatus,
    index
}) => {
    const compAbbreviation = PULSE.app.common.match
        .getCompAbbr(match?.compSeason?.providerId)
        ?.toLowerCase();

    const home = { ...match?.home };
    const away = { ...match?.away };
    const isSoldOut = match?.metadata?.sold_out === 'true';
    const prematchLabel = match?.metadata?.prematch_label ?? '';
    const hasHeaderBadges = prematchLabel || isSoldOut;

    const isGFRound = match?.round?.abbreviation === 'GF';

    const isLive =
        matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE
            ? true
            : false;

    if (liveMatch?.score) {
        home.liveScore = liveMatch.score.homeTeamScore?.matchScore?.totalScore;
        away.liveScore = liveMatch.score.awayTeamScore?.matchScore?.totalScore;
    }

    const { ladderData } = useGetLadderQuery(
        {
            compSeasonId: match.compSeason.id
        },
        {
            selectFromResult: ({ data }) => ({
                ladderData: data?.ladders
            }),
            skip:
                !match ||
                !PULSE.app.common.match.shouldShowPosition(matchStatus)
        }
    );

    const homePosition = useMemo(() => {
        if (ladderData && ladderData[0].entries) {
            return getLadderPosition(
                ladderData[0].entries,
                home.team.providerId
            );
        }
    }, [home, ladderData]);

    const awayPosition = useMemo(() => {
        if (ladderData && ladderData[0].entries) {
            return getLadderPosition(
                ladderData[0].entries,
                away.team.providerId
            );
        }
    }, [away, ladderData]);

    const cardsCount = useContext(FixtureCountContext);

    const compLogo = () => {
        if (compAbbreviation === 'afl') {
            return (
                <img
                    src={`${PULSE.app.environment.cdn}i/elements/afl-logo.png`}
                    className="icon fixtures__match-score-divider-icon fixtures__match-score-divider-icon--afl"
                    alt="afl logo"
                />
            );
        }

        return (
            <CompIcon
                code={compAbbreviation}
                className="icon fixtures__match-score-divider-icon"
            />
        );
    };

    const compLogoSection = () => {
        if (isGFRound) {
            return (
                <SvgIcon
                    icon="trophy"
                    className={'fixtures__match-score-divider__finals-hub'}
                />
            );
        }

        return (
            <div className="fixtures__match-score-divider-outline">
                <div
                    className={`fixtures__match-score-divider fixtures__match-score-divider--${compAbbreviation}`}
                >
                    {compLogo()}
                </div>
            </div>
        );
    };

    const headerBadges = () => {
        if (
            (matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING ||
                matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.TBC) &&
            hasHeaderBadges
        ) {
            return (
                <div className="fixtures__badges-container">
                    {prematchLabel && (
                        <FixturePrematchLabel
                            prematchLabel={prematchLabel}
                            isFinals={isGFRound}
                        />
                    )}
                    {isSoldOut && <FixtureSoldOut />}
                </div>
            );
        }

        return null;
    };

    const canLinkToMC = useCallback(() => {
        return (
            !PULSE.app.common.team.isPlaceholderTeam(home?.team?.providerId) &&
            !PULSE.app.common.team.isPlaceholderTeam(away?.team?.providerId)
        );
    }, [home, away]);

    return (
        <div className="fixtures__details">
            {canLinkToMC() && (
                <a
                    href={link}
                    className="fixtures__absolute-link"
                    aria-label={PULSE.app.common.matchAriaLabel({
                        ...match,
                        home: home,
                        away: away,
                        score: liveMatch?.score,
                        status: matchStatus
                    })}
                    onClick={() => {
                        PULSE.app.tracking.selectFixtureCardHandler(
                            match,
                            getTranslation('label.fixtures.card'),
                            link,
                            index,
                            cardsCount
                        );
                    }}
                ></a>
            )}

            <div className="fixtures__details-main">
                <div className="fixtures__responsive-team-versus">
                    <span>
                        {home?.team.name} v {away?.team.name}
                    </span>
                </div>

                <div className="fixtures__match-team fixtures__match-team--home">
                    <FixtureTeam
                        team={home?.team}
                        theme={isLive ? 'light' : ''}
                        conferenceLabel={getConferenceLabel(match)}
                        isFinals={isGFRound}
                    />
                </div>
                <div className="fixtures__match-scoreboard">
                    {isLive && (
                        <FixtureScore
                            score={liveMatch?.score?.homeTeamScore?.matchScore}
                        />
                    )}

                    {/* This also checks if live match has transitioned to completed */}
                    {matchStatus ===
                        PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED && (
                        <FixtureScore
                            score={
                                liveMatch?.score?.homeTeamScore?.matchScore
                                    ? liveMatch.score.homeTeamScore.matchScore
                                    : home?.score
                            }
                        />
                    )}

                    {PULSE.app.common.match.shouldShowPosition(matchStatus) && (
                        <FixturePosition position={homePosition} />
                    )}

                    {compLogoSection()}

                    {isLive && (
                        <FixtureScore
                            score={liveMatch?.score?.awayTeamScore?.matchScore}
                        />
                    )}

                    {/* This also checks if live match has transitioned to completed */}
                    {matchStatus ===
                        PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED && (
                        <FixtureScore
                            score={
                                liveMatch?.score?.awayTeamScore?.matchScore
                                    ? liveMatch.score.awayTeamScore.matchScore
                                    : away?.score
                            }
                        />
                    )}

                    {PULSE.app.common.match.shouldShowPosition(matchStatus) && (
                        <FixturePosition position={awayPosition} />
                    )}
                </div>

                <div className="fixtures__match-team fixtures__match-team--away">
                    <FixtureTeam
                        team={away?.team}
                        theme={isLive ? 'light' : ''}
                        conferenceLabel={getConferenceLabel(match, true)}
                        isFinals={isGFRound}
                    />
                </div>
            </div>

            {headerBadges()}

            {(matchStatus !==
                PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING ||
                matchStatus !==
                    PULSE.app.common.CONSTANTS.MATCH_STATUS.TBC) && (
                <FixtureSummary
                    home={home}
                    away={away}
                    isLive={isLive}
                    matchStatus={matchStatus}
                />
            )}

            {match.venue && <FixtureVenue venue={match.venue} />}
        </div>
    );
};

FixtureDetails.propTypes = {
    match: PropTypes.object.isRequired,
    liveMatch: PropTypes.object,
    matchStatus: PropTypes.string,
    link: PropTypes.string,
    index: PropTypes.number
};
